import { useState } from 'react'
import Wheel from './components/Wheel'
import ConfigPanel from './components/ConfigPanel'
import ResultDisplay from './components/ResultDisplay'
import ExampleWheels from './components/ExampleWheels'
import Header from './components/Header'
import Footer from './components/Footer'
import { motion } from 'framer-motion'
import { Edit3, Check, X, RotateCcw } from 'lucide-react'
import { useWheelConfig } from './hooks/useWheelConfig'
import { useWheelGame } from './hooks/useWheelGame'

function App() {
  // 使用自定义Hooks
  const {
    wheelItems,
    title,
    subtitle,
    handleItemsChange,
    updateTitle,
    updateSubtitle,
    loadExample,
    resetConfig
  } = useWheelConfig()

  const {
    isSpinning,
    result,
    showConfig,
    handleSpin,
    handleSpinComplete,
    handleCloseResult,
    setShowConfig
  } = useWheelGame()

  // 标题编辑状态
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [tempTitle, setTempTitle] = useState('')
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false)
  const [tempSubtitle, setTempSubtitle] = useState('')

  // 开始编辑标题
  const handleEditTitle = () => {
    setTempTitle(title)
    setIsEditingTitle(true)
  }

  // 保存标题
  const handleSaveTitle = () => {
    if (tempTitle.trim()) {
      updateTitle(tempTitle.trim())
    }
    setIsEditingTitle(false)
  }

  // 取消编辑标题
  const handleCancelEditTitle = () => {
    setTempTitle('')
    setIsEditingTitle(false)
  }

  // 开始编辑副标题
  const handleEditSubtitle = () => {
    setTempSubtitle(subtitle)
    setIsEditingSubtitle(true)
  }

  // 保存副标题
  const handleSaveSubtitle = () => {
    updateSubtitle(tempSubtitle.trim())
    setIsEditingSubtitle(false)
  }

  // 取消编辑副标题
  const handleCancelEditSubtitle = () => {
    setTempSubtitle('')
    setIsEditingSubtitle(false)
  }

  const canEditTitles = showConfig

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 flex flex-col">
      {/* 顶部导航栏 */}
      <Header />

      {/* 主要内容区域 */}
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
        {/* 标题 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle()
                    if (e.key === 'Escape') handleCancelEditTitle()
                  }}
                  className="text-4xl md:text-6xl font-bold text-slate-900 bg-transparent border-b-2 border-theme-300 focus:outline-none focus:border-theme-400 text-center min-w-0"
                  placeholder="输入标题"
                  autoFocus
                />
                <button
                  onClick={handleSaveTitle}
                  className="p-1 text-theme-600 hover:text-theme-700 hover:bg-theme-100 rounded transition-colors"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCancelEditTitle}
                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {canEditTitles && (
                  <div
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors invisible"
                    aria-hidden="true"
                  >
                    <Edit3 className="w-5 h-5" />
                  </div>
                )}
                <h1 className="text-4xl md:text-6xl font-bold text-slate-900">
                  {title}
                </h1>
                {canEditTitles && (
                  <button
                    onClick={handleEditTitle}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    title="编辑标题"
                  >
                    <Edit3 className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
          {/* 副标题 */}
          <div className="flex items-center justify-center gap-2 mb-2">
            {isEditingSubtitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={tempSubtitle}
                  onChange={(e) => setTempSubtitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveSubtitle()
                    if (e.key === 'Escape') handleCancelEditSubtitle()
                  }}
                  className="text-xl text-slate-600 bg-transparent border-b border-theme-200 focus:outline-none focus:border-theme-400 text-center min-w-0"
                  placeholder="输入副标题"
                  autoFocus
                />
                <button
                  onClick={handleSaveSubtitle}
                  className="p-1 text-theme-600 hover:text-theme-700 hover:bg-theme-100 rounded transition-colors"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCancelEditSubtitle}
                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {canEditTitles && (
                  <div
                    className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors invisible"
                    aria-hidden="true"
                  >
                    <Edit3 className="w-4 h-4" />
                  </div>
                )}
                <p className="text-xl text-slate-600">
                  {subtitle}
                </p>
                {canEditTitles && (
                  <button
                    onClick={handleEditSubtitle}
                    className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                    title="编辑副标题"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* 转盘容器 */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2, damping: 15 }}
          className="flex flex-col items-center"
        >
          <Wheel 
            items={wheelItems}
            isSpinning={isSpinning}
            onSpinComplete={handleSpinComplete}
          />
          
          {/* 控制按钮 */}
          <div className="mt-8 flex gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSpin}
              disabled={isSpinning}
              className="px-8 py-3 bg-theme-500 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-theme-600"
            >
              {isSpinning ? '转动中...' : '开始转动'}
            </motion.button>
            
            <motion.button
              whileHover={!isSpinning ? { scale: 1.05 } : {}}
              whileTap={!isSpinning ? { scale: 0.95 } : {}}
              onClick={() => !isSpinning && setShowConfig(!showConfig)}
              disabled={isSpinning}
              className="px-6 py-3 bg-white text-slate-700 font-bold rounded-full border border-slate-200 hover:bg-slate-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {showConfig ? '隐藏设置' : '设置'}
            </motion.button>
            
            <motion.button
              whileHover={!isSpinning ? { scale: 1.05 } : {}}
              whileTap={!isSpinning ? { scale: 0.95 } : {}}
              onClick={resetConfig}
              disabled={isSpinning}
              className="px-6 py-3 bg-white text-red-500 font-bold rounded-full border border-red-200 hover:bg-red-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              清空
            </motion.button>
          </div>
        </motion.div>

        {/* 配置面板 */}
        {showConfig && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="mt-8"
          >
            <ConfigPanel 
              items={wheelItems}
              onChange={handleItemsChange}
            />
          </motion.div>
        )}
        {/* 示例转盘选择器 */}
        <ExampleWheels onLoadExample={loadExample} />

        {/* 结果显示 */}
        {result && (
          <ResultDisplay result={result} onClose={handleCloseResult} />
        )}
        </div>
      </main>

      {/* 底部Footer */}
      <Footer />
    </div>
  )
}

export default App
